import { Button, CIInfoIcon, Pagination } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { SERVICE_MODE, URL_PATH } from '../../../../constants';
import { IExceptionModel } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { updatePageNo } from '../../../../store/reducer/services/ExceptionsReducer';
import { setIsBackButtonWasPressedOnException } from '../../../../store/reducer/services/ServiceOperationMonitorReducer';
import { getModeFromUrl, getVersionFromUrl } from '../../../../utilities/services/ServiceListUtils';
import { ExceptionList } from './exception-list/ExceptionList';

import classes from './ServiceExceptions.module.scss';

interface IServiceExceptionFormProps {
  serviceName: string | null;
  isExceptionDisabled: (status: boolean) => void
}
export const ServiceExceptions = ({
  serviceName,
  isExceptionDisabled,
}: IServiceExceptionFormProps) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const history = useNavigate();
  const location = useLocation();
  const queryParam = location.state?.path;
  const [isContinueDisabled, setContinueDisabled] = useState<boolean>(false);
  const serviceMode = getModeFromUrl();
  const serviceVersion = getVersionFromUrl();
  const isEditOrViewMode = serviceMode === SERVICE_MODE.VIEW || serviceMode === SERVICE_MODE.EDIT;
  const isEditOrCreateMode = serviceMode !== SERVICE_MODE.VIEW;

  const { exceptions, pageNo } = useSelector(
    (state) => state.exceptionReducer
  );
  const [exceptionList, setExceptionList] = useState<IExceptionModel[] | null>(null);
  const [localExceptionList, setLocalExceptionList] = useState<IExceptionModel[]>([]);
  const { isEditService } = useSelector((state) => state.serviceOperationMonitorReducer);

  const [page, setPage] = useState<number>(pageNo);

  useEffect(() => {
    setPage(pageNo);
  }, [pageNo]);

  const onPageNoUpdate = (pageNumber: number) => {
    setPage(pageNumber);
    dispatch(updatePageNo(pageNumber));
  };

  const onBackBtnClickHandler = () => {
    // need to concatenate 2 string constants due router V6 behavior
    dispatch(setIsBackButtonWasPressedOnException(true));
    if (isEditOrViewMode) {
      history(`${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/${serviceMode}`, { state: { ...location.state, path: queryParam } });
      return;
    }
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`, { state: location.state });
  };

  useEffect(() => {
    if (!isEditOrCreateMode) {
      const list = exceptions.filter((exception, index) => (page * 5) <= index && index < (page * 5) + 5);
      setExceptionList(list);
    } else if (isEditOrCreateMode) {
      const list = exceptions.filter((exception, index) => (page * 5) <= index && index < (page * 5) + 5);
      setLocalExceptionList(list);
    }
  }, [exceptions, page, isEditService]);

  const onSaveHandler = () => {
    // need to concatenate 2 string constants due router V6 behavior
    if (isEditOrViewMode) {
      history(`${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE_OPERATION}/${serviceName}/${serviceVersion}/${serviceMode}`, { state: { ...location.state, path: queryParam } });
      return;
    }
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE_OPERATION}`, { state: location.state });
  };

  const continueHandler = (status: boolean) => {
    setContinueDisabled(status);
    isExceptionDisabled(status);
  };

  return (
    <div className={`form-wrapper-container ${classes['service-exceptions']}`}>
      <Row>
        <div className={classes['exception-title']}>
          {translate('service_exceptions.exception_header')}
        </div>
        <div className={classes['exception-sub-title']}>
          {translate('service_exceptions.exception_sub_title')}
        </div>
      </Row>
      <div className={classes['exception-list']}>
        <Row className={classes['list-header']}>
          <Col sm={4}>
            {translate('service_exceptions.exception_name')}
            <CIInfoIcon iconTxt={String(translate('service_exceptions.exception_name_info_bubble'))} />
          </Col>
          <Col sm={3}>
            {translate('service_exceptions.error_code')}
            <CIInfoIcon iconTxt={String(translate('service_exceptions.error_code_info_bubble'))} />
          </Col>
          <Col sm={4}>
            {translate('service_exceptions.action')}
            <CIInfoIcon iconTxt={String(translate('service_exceptions.action_info_bubble'))} />
          </Col>
          <Col sm={1} />
        </Row>
        <ExceptionList exceptionList={exceptionList} continueHandler={continueHandler} isEditService={isEditService} localExceptionList={localExceptionList} />
        {exceptions.length > 0 && (
          <Row className={classes['exception-pagination']}>
            <Pagination selectItems={[5]} itemsPerPage={5} currentPageNumber={page} numberOfPages={Math.ceil(exceptions.length / 5)} onCurrentPageNumberChange={onPageNoUpdate} onItemsPerPageChange={() => { }} key={page + 1} />
          </Row>
        )}
      </div>
      <div className={`${classes[isEditOrViewMode ? 'btn-container-view' : 'btn-container']} ci-button-group`}>
        <Button variant="secondary" className={classes['btn-back']} onClick={onBackBtnClickHandler} data-testid="back-btn">{translate('service_exceptions.txt_back')}</Button>
        <span className={classes['btn-save-container']}>
          <Button variant="primary" className={classes['btn-save-continue']} disabled={isContinueDisabled} onClick={() => onSaveHandler()} data-testid="save-btn">
            {isEditOrViewMode ? translate('multi_form_modal_component.btn_next') : translate('service_exceptions.save_and_continue')}
          </Button>
        </span>
      </div>
    </div>
  );
};
